import { ChangeDetectorRef, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material';
import { ActivatedRoute } from '@angular/router';
import { interval, timer } from 'rxjs';
import { switchMap, take, takeUntil, takeWhile } from 'rxjs/operators';
import { CanisterTypeHttpService } from '../../../../core/settings/_services/canister-type-http.service';
import { RegisterMachineHttpService } from '../../../../core/register-machine/_services/register-machine-http.service';
import { CANISTER_TYPES, canisterStateLabel, canisterTypeErrorMessage, canSendCanisterType, currentTypeText, displayCanisterType, isApplied, isAwaitingMachine, isCanisterTypeName, machineTypeSource, notConfirmedDetail, registeredCanisterType, waitingDetail } from '../../../../core/settings/_services/canister-type.util';
import { MixpanelService } from '../../../../services/mixpanel.service';
import { MachineRouteService } from '../machine-route.service';
import { CanisterTypeConfirmDialogComponent } from './canister-type-confirm-dialog.component';
var CanisterTypeComponent = /** @class */ (function () {
    function CanisterTypeComponent(route, machineRouteService, http, registrationHttp, dialog, cdr, mixpanelService) {
        this.route = route;
        this.machineRouteService = machineRouteService;
        this.http = http;
        this.registrationHttp = registrationHttp;
        this.dialog = dialog;
        this.cdr = cdr;
        this.mixpanelService = mixpanelService;
        this.typeOptions = CANISTER_TYPES.map(function (type) { return ({ label: type, value: type }); });
        this.serialNo = '';
        this.machineName = '';
        this.state = null;
        // From the registration record. Shown only when the machine has not reported a type.
        this.registered = null;
        this.selection = null;
        this.isLoading = false;
        this.loadError = '';
        this.phase = 'idle';
        this.statusMessage = '';
        this.destroyed = false;
        this.selectionSet = false;
        this.pollSubscription = null;
    }
    CanisterTypeComponent.prototype.ngOnInit = function () {
        var machine = this.machineRouteService.getMachine(this.route);
        if (!machine) {
            this.loadError = 'Open this page from a machine.';
            return;
        }
        this.serialNo = machine.serial_no ? String(machine.serial_no) : '';
        this.machineName = machine.machine_name ? String(machine.machine_name) : '';
        this.mixpanelService.track(this.mixpanelService.PAGE_VIEW, {
            machine_id: machine.machine_id,
            machine_name: machine.machine_name,
            machine_serial_no: this.serialNo
        });
        if (!this.serialNo) {
            this.loadError = 'This machine has no serial number, so its canister type cannot be read or changed from here.';
            return;
        }
        this.loadState(false);
        this.loadRegistered();
    };
    CanisterTypeComponent.prototype.ngOnDestroy = function () {
        this.destroyed = true;
        this.stopWatching();
    };
    Object.defineProperty(CanisterTypeComponent.prototype, "formLocked", {
        get: function () {
            return this.phase === 'sending';
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CanisterTypeComponent.prototype, "canSend", {
        get: function () {
            return canSendCanisterType(this.state, this.selection, this.formLocked);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CanisterTypeComponent.prototype, "selectionIsRequested", {
        // The selection is the type already requested, so there is nothing to send.
        get: function () {
            return !!this.state && !!this.selection && this.selection === this.state.desired;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CanisterTypeComponent.prototype, "switchLabel", {
        get: function () {
            return this.selection ? 'Switch to ' + this.selection : 'Switch';
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CanisterTypeComponent.prototype, "waitingTitle", {
        get: function () {
            return 'Waiting for the machine to apply ' + displayCanisterType(this.state ? this.state.desired : null);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CanisterTypeComponent.prototype, "waitingText", {
        get: function () {
            return waitingDetail(this.state ? this.state.reported : null);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CanisterTypeComponent.prototype, "notConfirmedText", {
        get: function () {
            return notConfirmedDetail(this.state ? this.state.desired : null, this.state ? this.state.reported : null);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CanisterTypeComponent.prototype, "machineSource", {
        get: function () {
            return machineTypeSource(this.state ? this.state.reported : null, this.registered);
        },
        enumerable: true,
        configurable: true
    });
    CanisterTypeComponent.prototype.hasType = function (value) {
        return isCanisterTypeName(value);
    };
    CanisterTypeComponent.prototype.typeLabel = function (value) {
        return displayCanisterType(value);
    };
    CanisterTypeComponent.prototype.stateLabel = function () {
        return this.state ? canisterStateLabel(this.state) : '';
    };
    CanisterTypeComponent.prototype.onSelectionChange = function (value) {
        this.selection = value;
        this.selectionSet = true;
        if (this.phase === 'failed' || this.phase === 'applied') {
            this.phase = this.state && isAwaitingMachine(this.state) ? 'waiting' : 'idle';
            this.statusMessage = '';
        }
    };
    CanisterTypeComponent.prototype.onRefresh = function () {
        if (this.formLocked || !this.serialNo) {
            return;
        }
        this.loadState(false);
    };
    // After polling gives up. Looks again and, if still pending, watches for another minute.
    CanisterTypeComponent.prototype.onCheckAgain = function () {
        this.onRefresh();
    };
    CanisterTypeComponent.prototype.onSwitch = function () {
        var _this = this;
        if (!this.canSend || !this.state || !this.selection) {
            return;
        }
        var nextType = this.selection;
        var dialogRef = this.dialog.open(CanisterTypeConfirmDialogComponent, {
            width: '480px',
            data: {
                serialNo: this.serialNo,
                currentText: currentTypeText(this.state.reported, this.registered),
                nextType: nextType
            }
        });
        dialogRef.afterClosed().subscribe(function (result) {
            if (_this.destroyed || !result || !result.confirmed) {
                return;
            }
            _this.send(nextType);
        });
    };
    CanisterTypeComponent.prototype.send = function (canisterType) {
        var _this = this;
        this.stopWatching();
        this.phase = 'sending';
        this.statusMessage = 'Sending…';
        this.cdr.detectChanges();
        this.http.setCanisterType(this.serialNo, canisterType).subscribe({
            next: function () {
                if (_this.destroyed) {
                    return;
                }
                _this.loadState(true);
            },
            error: function (error) {
                if (_this.destroyed) {
                    return;
                }
                _this.phase = 'failed';
                _this.statusMessage = canisterTypeErrorMessage(error, 'change');
                _this.cdr.detectChanges();
            }
        });
    };
    // The card still works without it: it shows Unknown when nothing is reported.
    CanisterTypeComponent.prototype.loadRegistered = function () {
        var _this = this;
        this.registrationHttp.getRegistration(this.serialNo).pipe(take(1)).subscribe({
            next: function (rows) {
                if (_this.destroyed) {
                    return;
                }
                // Not preselected: switching to the type it is only assumed to have should be a deliberate pick.
                _this.registered = registeredCanisterType(rows);
                _this.cdr.detectChanges();
            },
            error: function () { }
        });
    };
    CanisterTypeComponent.prototype.loadState = function (afterSend) {
        var _this = this;
        if (!afterSend) {
            this.isLoading = !this.state;
            this.loadError = '';
        }
        this.http.getCanisterType(this.serialNo).subscribe({
            next: function (nextState) {
                if (_this.destroyed) {
                    return;
                }
                _this.state = nextState;
                _this.isLoading = false;
                _this.loadError = '';
                if (!_this.selectionSet) {
                    _this.selection = _this.initialSelection(nextState);
                    _this.selectionSet = true;
                }
                _this.applyLoadedState(afterSend);
                _this.cdr.detectChanges();
            },
            error: function (error) {
                if (_this.destroyed) {
                    return;
                }
                _this.isLoading = false;
                if (afterSend) {
                    _this.phase = 'failed';
                    _this.statusMessage = 'The request was sent, but the cloud could not be checked afterward. Refresh before trying again.';
                }
                else if (!_this.state) {
                    _this.loadError = canisterTypeErrorMessage(error, 'load');
                }
                else {
                    _this.phase = 'failed';
                    _this.statusMessage = canisterTypeErrorMessage(error, 'load');
                }
                _this.cdr.detectChanges();
            }
        });
    };
    CanisterTypeComponent.prototype.applyLoadedState = function (afterSend) {
        if (isAwaitingMachine(this.state)) {
            this.phase = 'waiting';
            this.statusMessage = '';
            this.watchUntilSettled();
            return;
        }
        this.stopWatching();
        if (isApplied(this.state) && afterSend) {
            this.phase = 'applied';
            this.statusMessage = 'The machine reported ' + displayCanisterType(this.state.reported) + '.';
            return;
        }
        this.phase = 'idle';
        this.statusMessage = '';
    };
    CanisterTypeComponent.prototype.watchUntilSettled = function () {
        var _this = this;
        this.stopWatching();
        this.pollSubscription = interval(3000).pipe(takeUntil(timer(60000)), switchMap(function () { return _this.http.getCanisterType(_this.serialNo); }), takeWhile(function (nextState) { return isAwaitingMachine(nextState); }, true)).subscribe({
            next: function (nextState) {
                if (_this.destroyed) {
                    return;
                }
                _this.state = nextState;
                if (!isAwaitingMachine(nextState) && isApplied(nextState)) {
                    _this.phase = 'applied';
                    _this.statusMessage = 'The machine reported ' + displayCanisterType(nextState.reported) + '.';
                    _this.stopWatching();
                }
                else if (isAwaitingMachine(nextState)) {
                    _this.phase = 'waiting';
                }
                else {
                    _this.phase = 'idle';
                    _this.statusMessage = '';
                }
                _this.cdr.detectChanges();
            },
            error: function () {
                if (_this.destroyed) {
                    return;
                }
                _this.phase = 'failed';
                _this.statusMessage = 'Could not check whether the machine applied the change. Refresh to look again.';
                _this.cdr.detectChanges();
            },
            complete: function () {
                if (_this.destroyed || _this.phase !== 'waiting') {
                    return;
                }
                _this.phase = 'not_confirmed';
                _this.pollSubscription = null;
                _this.cdr.detectChanges();
            }
        });
    };
    CanisterTypeComponent.prototype.initialSelection = function (nextState) {
        if (isCanisterTypeName(nextState.desired)) {
            return nextState.desired;
        }
        if (isCanisterTypeName(nextState.reported)) {
            return nextState.reported;
        }
        return null;
    };
    CanisterTypeComponent.prototype.stopWatching = function () {
        if (this.pollSubscription) {
            this.pollSubscription.unsubscribe();
            this.pollSubscription = null;
        }
    };
    return CanisterTypeComponent;
}());
export { CanisterTypeComponent };
